// src/utils/exertion/recovery.js

/**
 * Exertion recovery utilities
 *
 * Model (when available):
 *   y(t) = A * exp(-t / tau) + C
 * where A = initial deviation from baseline at t=0, C = baseline, tau in seconds.
 */

/* ---------------------------- small utilities ----------------------------- */

/**
 * Convert seconds to a { m, s, label } object for quick UI use.
 * @param {number} seconds
 * @returns {{m:number, s:number, label:string}}
 */
export function secondsToMinSec(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return { m, s: rem, label: `${m}:${rem}` };
}

/**
 * Extracts exponential parameters from your data object.
 * @param {any} data
 * @returns {{A:number, C:number, tau:number, y0:number} | null}
 */
export function getDecayParams(data) {
  const exp = data?.decay_fit?.exp;
  const A = Number(exp?.A);
  const C = Number(exp?.C);
  const tau = Number(exp?.tau);
  if (!isFinite(A) || !isFinite(C) || !isFinite(tau) || tau <= 0) return null;
  return { A, C, tau, y0: C + A };
}

/**
 * Evaluate the exponential at time t (seconds from recovery start).
 * @param {{A:number, C:number, tau:number}} p
 * @param {number} t
 * @returns {number}
 */
export function expAt(p, t) {
  const { A, C, tau } = p || {};
  if (!isFinite(A) || !isFinite(C) || !isFinite(tau) || tau <= 0) return NaN;
  return C + A * Math.exp(-t / tau);
}

/**
 * Time (seconds) to reach a remaining fraction of the initial deviation.
 * remainingFrac = (y(t) - C) / A (e.g., 0.10 => 90% recovered)
 * @param {{A:number, C:number, tau:number}} p
 * @param {number} remainingFrac in (0,1)
 * @returns {number}
 */
export function timeToRemainingFraction(p, remainingFrac) {
  const { A, tau } = p || {};
  if (!isFinite(A) || !isFinite(tau) || tau <= 0) return NaN;
  if (!(remainingFrac > 0 && remainingFrac < 1)) return NaN;
  return tau * Math.log(1 / remainingFrac);
}

/**
 * Time (seconds) to be within a given absolute bpm distance of baseline C.
 * Solves y(t) - C = tolBpm
 * @param {{A:number, C:number, tau:number}} p
 * @param {number} tolBpm
 * @returns {number}
 */
export function timeToWithinBpm(p, tolBpm = 1) {
  const { A, tau } = p || {};
  const tol = Number(tolBpm);
  if (!isFinite(A) || !isFinite(tau) || tau <= 0) return NaN;
  if (!(isFinite(tol) && tol > 0) || tol >= A) return NaN;
  return tau * Math.log(A / tol);
}

/* ----------------------- series fallback / interpolation ------------------ */

/**
 * Best guess of the recovery start time (absolute seconds)
 * Prefer data.t0, else first peak time, else 0.
 * @param {any} data
 * @returns {number}
 */
export function getRecoveryStartAbsTime(data) {
  const t0 = Number(data?.t0);
  if (isFinite(t0)) return t0;
  const first = Number(
    Array.isArray(data?.peak_times_s) ? data.peak_times_s[0] : NaN
  );
  if (isFinite(first)) return first;
  return 0;
}

/**
 * Linear interpolation of a series at absolute time tAbs.
 * @param {number[]} times absolute seconds (monotonic increasing)
 * @param {number[]} values bpm values aligned to times
 * @param {number} tAbs absolute seconds
 * @returns {number} interpolated y or NaN if out of range
 */
export function interpAt(times, values, tAbs) {
  if (!Array.isArray(times) || !Array.isArray(values)) return NaN;
  const n = Math.min(times.length, values.length);
  if (n < 2 || !isFinite(tAbs)) return NaN;
  if (tAbs < times[0] || tAbs > times[n - 1]) return NaN;

  // binary search for i such that times[i] <= tAbs <= times[i+1]
  let lo = 0,
    hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= tAbs) lo = mid;
    else hi = mid;
  }
  const t0 = times[lo],
    t1 = times[lo + 1];
  const y0 = values[lo],
    y1 = values[lo + 1];
  if (!isFinite(t0) || !isFinite(t1) || t1 === t0) return NaN;
  const a = (tAbs - t0) / (t1 - t0);
  return y0 + a * (y1 - y0);
}

/**
 * Get RR at an absolute time using model if possible, else interpolate peaks.
 * @param {any} data
 * @param {number} tAbs absolute seconds from video/trace timeline
 * @returns {number}
 */
export function rrAtAbsoluteTime(data, tAbs) {
  const params = getDecayParams(data);
  const t0Abs = getRecoveryStartAbsTime(data);

  // Try model first (t measured from recovery start)
  if (params && isFinite(tAbs) && isFinite(t0Abs)) {
    const tRel = tAbs - t0Abs;
    if (tRel >= 0) {
      return expAt(params, tRel);
    }
  }

  // Fallback to series interpolation
  const times = Array.isArray(data?.peak_times_s)
    ? data.peak_times_s.map(Number)
    : null;
  const vals = Array.isArray(data?.peak_values)
    ? data.peak_values.map(Number)
    : null;
  return interpAt(times, vals, tAbs);
}

/* ---------------------------- main API functions -------------------------- */

/**
 * Apple-style recovery window:
 * drop = RR(t0) - RR(t0 + windowSec)
 * Uses the model when available, otherwise falls back to interpolation.
 * @param {any} data
 * @param {number} windowSec default 60
 * @returns {{ ok:boolean, windowSec:number, rrStart:number, rrEnd:number, drop:number } }
 */
export function computeRRChangeInWindow(data, windowSec = 60, chosen) {
  const w = Number.isFinite(windowSec) ? Number(windowSec) : 60;
  const t0Abs = getRecoveryStartAbsTime(data);
  const rrStart = rrAtAbsoluteTimeUsing(data, t0Abs, chosen);
  const rrEnd = rrAtAbsoluteTimeUsing(data, t0Abs + w, chosen);

  const ok = isFinite(rrStart) && isFinite(rrEnd);
  return {
    ok,
    windowSec: w,
    rrStart: ok ? rrStart : NaN,
    rrEnd: ok ? rrEnd : NaN,
    drop: ok ? rrStart - rrEnd : NaN,
  };
}

/**
 * Primary helper: compute recovery metrics used by the UI.
 *
 * @param {any} data  The exertion result JSON.
 * @param {{minute?:number, frac90?:number, frac95?:number, withinBpm?:number}} [opts]
 * @returns {{
 *   ok: boolean,
 *   reason?: string,
 *   params?: { A:number, C:number, tau:number, y0:number },
 *   oneMinute?: { dropBpm:number, rrAtT:number, t:number },
 *   appleRecovery?: {
 *     w60: { ok:boolean, drop:number, rrStart:number, rrEnd:number },
 *     w120:{ ok:boolean, drop:number, rrStart:number, rrEnd:number }
 *   },
 *   timeToBaseline?: {
 *     seconds90:number, minutes90:number,
 *     seconds95:number, minutes95:number,
 *     secondsWithinBpm:number, minutesWithinBpm:number,
 *     withinBpm:number
 *   }
 * }}
 */
export function computeRecoveryMetrics(data, opts = {}) {
  const minute = Number.isFinite(opts.minute) ? Number(opts.minute) : 60;
  const frac90 = Number.isFinite(opts.frac90) ? opts.frac90 : 0.1; // 90% recovered
  const frac95 = Number.isFinite(opts.frac95) ? opts.frac95 : 0.05; // 95% recovered
  const withinBpm = Number.isFinite(opts.withinBpm) ? opts.withinBpm : 1; // within 1 bpm of C

  const params = getDecayParams(data);
  if (!params)
    return {
      ok: false,
      reason: "Missing or invalid decay_fit.exp parameters.",
    };

  const { A, C, tau, y0 } = params;

  // 1) Change in RR in 1 minute (model-based)
  const rrAtT = expAt(params, minute);
  const dropBpm = isFinite(rrAtT) ? y0 - rrAtT : NaN;

  // 1b) Apple-style RR recovery windows using model or interpolation
  const w60 = computeRRChangeInWindow(data, 60);
  const w120 = computeRRChangeInWindow(data, 120);

  // 2) Estimated time to “baseline”
  const t90 = timeToRemainingFraction(params, frac90);
  const t95 = timeToRemainingFraction(params, frac95);
  const tWithin = timeToWithinBpm(params, withinBpm);

  return {
    ok: true,
    params,
    oneMinute: {
      dropBpm, // bpm decrease over first minute (model)
      rrAtT, // RR at +1 minute (model)
      t: minute,
    },
    rrRecovery: {
      w60: {
        ok: w60.ok,
        drop: w60.drop,
        rrStart: w60.rrStart,
        rrEnd: w60.rrEnd,
      },
      w120: {
        ok: w120.ok,
        drop: w120.drop,
        rrStart: w120.rrStart,
        rrEnd: w120.rrEnd,
      },
    },
    timeToBaseline: {
      seconds90: t90,
      minutes90: isFinite(t90) ? t90 / 60 : NaN,
      seconds95: t95,
      minutes95: isFinite(t95) ? t95 / 60 : NaN,
      secondsWithinBpm: tWithin,
      minutesWithinBpm: isFinite(tWithin) ? tWithin / 60 : NaN,
      withinBpm,
    },
  };
}

/**
 * Convenience formatter for UI display (optional).
 * @param {ReturnType<typeof computeRecoveryMetrics>} m
 */
export function formatRecoveryMetrics(m) {
  if (!m?.ok) return { summary: "–", details: {} };

  const { oneMinute, timeToBaseline, appleRecovery } = m;
  const mm1 = secondsToMinSec(oneMinute?.t ?? 60);
  const t90 = secondsToMinSec(timeToBaseline?.seconds90);
  const t95 = secondsToMinSec(timeToBaseline?.seconds95);
  const tw = secondsToMinSec(timeToBaseline?.secondsWithinBpm);

  const w60 = appleRecovery?.w60;
  const w120 = appleRecovery?.w120;

  return {
    summary: `ΔRR @ ${mm1.label}: ${
      isFinite(oneMinute?.dropBpm) ? oneMinute.dropBpm.toFixed(2) : "–"
    } bpm`,
    details: {
      rrAt1min: isFinite(oneMinute?.rrAtT) ? oneMinute.rrAtT.toFixed(2) : "–",
      apple60:
        w60?.ok && isFinite(w60.drop) ? `${w60.drop.toFixed(2)} bpm` : "–",
      apple120:
        w120?.ok && isFinite(w120.drop) ? `${w120.drop.toFixed(2)} bpm` : "–",
      t90: t90.label,
      t95: t95.label,
      tWithin: tw.label,
      withinBpm: timeToBaseline?.withinBpm ?? 1,
    },
  };
}

function rssOnPeaks(yhat, times, values) {
  const n = Math.min(
    yhat?.length || 0,
    times?.length || 0,
    values?.length || 0
  );
  if (n < 2) return { n: 0, rss: Infinity };
  let rss = 0;
  for (let i = 0; i < n; i++) {
    const e = (values[i] ?? NaN) - (yhat[i] ?? NaN);
    if (!Number.isFinite(e)) continue;
    rss += e * e;
  }
  return { n, rss: Number.isFinite(rss) ? rss : Infinity };
}

function aic(n, rss, k) {
  if (n <= 0 || !Number.isFinite(rss) || rss <= 0) return Infinity;
  return n * Math.log(rss / n) + 2 * k;
}

function isNonIncreasing(arr, tol = 1e-6) {
  if (!Array.isArray(arr) || arr.length < 2) return true;
  for (let i = 1; i < arr.length; i++) {
    if (!Number.isFinite(arr[i]) || !Number.isFinite(arr[i - 1])) continue;
    if (arr[i] - arr[i - 1] > tol) return false;
  }
  return true;
}

export function validateExpFit(data) {
  const exp = data?.decay_fit?.exp;
  const A = Number(exp?.A),
    C = Number(exp?.C),
    tau = Number(exp?.tau);
  const dur = Number(data?.duration_s_full);

  if (
    !Number.isFinite(A) ||
    !Number.isFinite(C) ||
    !Number.isFinite(tau) ||
    tau <= 0
  ) {
    return { ok: false, reason: "non-finite params" };
  }

  // Sensible physiological bounds (tune to your population)
  if (!(C >= 3 && C <= 30)) return { ok: false, reason: "C out of range" };
  if (!(A > 0 && A <= 60)) return { ok: false, reason: "A out of range" };

  // Tau relative to test duration
  if (Number.isFinite(dur) && dur > 0) {
    if (tau < 0.5 || tau > 10 * dur)
      return { ok: false, reason: "tau unreasonable vs duration" };
  } else {
    if (tau < 0.5 || tau > 600)
      return { ok: false, reason: "tau unreasonable (no duration)" };
  }

  // Non-increasing yhat expected for recovery
  const yhat = Array.isArray(exp?.yhat) ? exp.yhat.map(Number) : null;
  if (!isNonIncreasing(yhat, 1e-3)) {
    return { ok: false, reason: "exp yhat not non-increasing" };
  }

  // Extreme magnitudes (catch pathological solutions)
  if (Math.abs(A) > 200 || Math.abs(C) > 80) {
    return { ok: false, reason: "magnitudes absurd" };
  }

  return { ok: true, reason: "ok" };
}

/**
 * Choose best recovery model: 'exp' | 'linear' | 'interp'
 * Returns { kind, series, params?, diagnostics }
 * - series: array of {x, y} aligned to peak_times_s length for charting
 * - params: only for 'exp' (A,C,tau)
 */
export function chooseRecoveryModel(data) {
  const times = Array.isArray(data?.peak_times_s)
    ? data.peak_times_s.map(Number)
    : [];
  const values = Array.isArray(data?.peak_values)
    ? data.peak_values.map(Number)
    : [];
  const n = Math.min(times.length, values.length);
  if (n < 2)
    return {
      kind: "interp",
      series: [],
      diagnostics: { reason: "insufficient data" },
    };

  // Build yhat arrays aligned to times for both models (truncate to n)
  const exp = data?.decay_fit?.exp || {};
  const lin = data?.decay_fit?.linear || {};

  const yhatExp = Array.isArray(exp?.yhat)
    ? exp.yhat.slice(0, n).map(Number)
    : null;
  const yhatLin = Array.isArray(lin?.yhat)
    ? lin.yhat.slice(0, n).map(Number)
    : null;

  // Validate exp
  const expValid = validateExpFit(data);
  const useableExp = expValid.ok && yhatExp && yhatExp.length === n;

  // Score both with AIC (RSS on peaks)
  const { rss: rssExp, n: nExp } = useableExp
    ? rssOnPeaks(yhatExp, times, values)
    : { rss: Infinity, n: 0 };
  const { rss: rssLin, n: nLin } = yhatLin
    ? rssOnPeaks(yhatLin, times, values)
    : { rss: Infinity, n: 0 };

  let AIC_EXP = useableExp ? aic(nExp, rssExp, 3) : Infinity;
  const AIC_LIN = Number.isFinite(rssLin) ? aic(nLin, rssLin, 2) : Infinity;

  // R² (already in your payload)
  const r2Exp = Number(exp?.r2);
  const r2Lin = Number(lin?.r2);

  // --- Exponential priors (shape + approach to baseline) ---
  const expMonotone = isNonIncreasing(yhatExp, 1e-3);
  const expConvex = isWeaklyConvex(yhatExp, 1e-3);
  const tau = Number(exp?.tau);
  const dur = Number(data?.duration_s_full);
  const tauReasonable =
    Number.isFinite(tau) &&
    (Number.isFinite(dur) && dur > 0
      ? tau >= 0.5 && tau <= 10 * dur
      : tau >= 0.5 && tau <= 600);
  const expShapeOK = expMonotone && expConvex && tauReasonable;

  // Apply a small prior bonus to exp if it passes shape checks
  if (useableExp && expShapeOK) {
    AIC_EXP -= 1.0; // ~one point prior towards exp
  }

  // Decision
  const deltaAIC = AIC_EXP - AIC_LIN; // negative favors exp
  const deltaR2 = r2Exp - r2Lin; // positive favors exp

  let kind = "linear";
  let reason = "";

  if (useableExp) {
    const expBeatsByAIC = deltaAIC <= -1; // relaxed towards exp
    const expBeatsByR2 = deltaR2 >= 0.02; // relaxed towards exp
    const nearTie = deltaAIC > -1 && deltaAIC < +2;
    if (expBeatsByAIC || expBeatsByR2 || (nearTie && expShapeOK)) {
      kind = "exp";
      reason = "exp preferred (AIC/R²/priors)";
    } else {
      kind = "linear";
      reason = "linear chosen: exp advantage not meaningful after bias";
    }
  } else {
    // Exp invalid → linear if usable, else interpolation
    if (Number.isFinite(AIC_LIN)) {
      kind = "linear";
      reason = `exp invalid (${expValid.reason})`;
    } else {
      kind = "interp";
      reason = "both fits unusable";
    }
  }

  const series =
    kind === "exp" && yhatExp
      ? times.map((x, i) => ({ x, y: yhatExp[i] }))
      : kind === "linear" && yhatLin
      ? times.map((x, i) => ({ x, y: yhatLin[i] }))
      : []; // interp will be handled elsewhere

  const params =
    kind === "exp"
      ? { A: Number(exp?.A), C: Number(exp?.C), tau: Number(exp?.tau) }
      : null;

  return {
    kind,
    series,
    params,
    diagnostics: {
      reason,
      r2Exp,
      r2Lin,
      AIC_EXP,
      AIC_LIN,
      expValid: expValid.reason,
      priors: {
        expMonotone,
        expConvex,
        tauReasonable,
        nearTie: deltaAIC > -1 && deltaAIC < +2,
      },
    },
  };
}

function median(arr) {
  const v = (arr || [])
    .filter(Number.isFinite)
    .slice()
    .sort((a, b) => a - b);
  if (!v.length) return NaN;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : 0.5 * (v[mid - 1] + v[mid]);
}

export function getLinearParams(data) {
  const lin = data?.decay_fit?.linear;
  const m = Number(lin?.m);
  const b = Number(lin?.b);
  if (!Number.isFinite(m) || !Number.isFinite(b)) return null;
  return { m, b };
}

/**
 * Robust baseline from tail of peaks (last 20% or at least 5 points).
 * Falls back to median of all peaks if too few.
 */
export function estimateLinearBaseline(data) {
  const vals = Array.isArray(data?.peak_values)
    ? data.peak_values.map(Number)
    : [];
  const n = vals.length;
  if (n < 3) return NaN;
  const k = Math.max(5, Math.floor(n * 0.2));
  const tail = vals.slice(Math.max(0, n - k));
  let C = median(tail);
  if (!Number.isFinite(C)) C = median(vals);
  return C;
}

/** Evaluate linear fit at absolute time tAbs. */
export function linearAtAbs(p, tAbs) {
  if (!p || !Number.isFinite(tAbs)) return NaN;
  const { m, b } = p;
  if (!Number.isFinite(m) || !Number.isFinite(b)) return NaN;
  return m * tAbs + b;
}

/** RR at t0 (start), preferring the model used. */
export function rrAtStart(data, chosen) {
  const t0Abs = getRecoveryStartAbsTime(data);
  if (!Number.isFinite(t0Abs)) return NaN;

  if (chosen?.kind === "exp") {
    const exp = getDecayParams(data);
    return expAt(exp, 0); // tRel = 0
  }
  if (chosen?.kind === "linear") {
    const p = getLinearParams(data);
    return linearAtAbs(p, t0Abs);
  }

  // Interp fallback
  const times = Array.isArray(data?.peak_times_s)
    ? data.peak_times_s.map(Number)
    : null;
  const vals = Array.isArray(data?.peak_values)
    ? data.peak_values.map(Number)
    : null;
  return interpAt(times, vals, t0Abs);
}

/**
 * Time to fraction remaining of initial deviation for LINEAR model.
 * r in (0,1): y = C_lin + r*(y0 - C_lin)
 * Returns seconds relative to t0Abs.
 */
export function timeToRemainingFractionLinear(data, r) {
  const p = getLinearParams(data);
  const t0Abs = getRecoveryStartAbsTime(data);
  const C = estimateLinearBaseline(data);
  if (!p || !Number.isFinite(t0Abs) || !Number.isFinite(C)) return NaN;

  const y0 = linearAtAbs(p, t0Abs);
  if (!Number.isFinite(y0)) return NaN;
  const A = y0 - C;
  if (!(A > 0) || !(r > 0 && r < 1)) return NaN;
  if (!(p.m < 0)) return NaN; // must be decreasing to "recover"

  const target = C + r * A;
  const tAbs = (target - p.b) / p.m;
  const tRel = tAbs - t0Abs;
  if (!Number.isFinite(tRel) || tRel < 0) return NaN;

  // Keep within observed window if available
  const tLast = Array.isArray(data?.peak_times_s)
    ? data.peak_times_s.at(-1)
    : NaN;
  if (Number.isFinite(tLast) && tAbs > tLast + 1e-6) return NaN;
  return tRel;
}

/**
 * Time to be within +withinBpm of baseline for LINEAR model.
 * Returns seconds relative to t0Abs.
 */
export function timeToWithinBpmLinear(data, withinBpm = 1) {
  const p = getLinearParams(data);
  const t0Abs = getRecoveryStartAbsTime(data);
  const C = estimateLinearBaseline(data);
  if (!p || !Number.isFinite(t0Abs) || !Number.isFinite(C)) return NaN;
  if (!(p.m < 0)) return NaN; // need decreasing

  const y0 = linearAtAbs(p, t0Abs);
  if (!Number.isFinite(y0)) return NaN;

  const A = y0 - C; // initial excess over baseline
  if (
    !(A > 0) ||
    !Number.isFinite(withinBpm) ||
    withinBpm <= 0 ||
    withinBpm >= A
  )
    return NaN;

  const target = C + withinBpm;
  const tAbs = (target - p.b) / p.m;
  const tRel = tAbs - t0Abs;
  if (!Number.isFinite(tRel) || tRel < 0) return NaN;

  const tLast = Array.isArray(data?.peak_times_s)
    ? data.peak_times_s.at(-1)
    : NaN;
  if (Number.isFinite(tLast) && tAbs > tLast + 1e-6) return NaN;
  return tRel;
}

// ============== Unified metrics that respect chosen model ==============

/**
 * Compute metrics using the selected model.
 * - If 'exp' → identical to your previous computeRecoveryMetrics().
 * - If 'linear' → uses linear formulas above (no tau).
 * - If 'interp' → only window metrics (w60/w120); time-to-baseline unavailable.
 */
export function computeRecoveryMetricsChosen(data, opts = {}) {
  const minute = Number.isFinite(opts.minute) ? Number(opts.minute) : 60;
  const frac90 = Number.isFinite(opts.frac90) ? opts.frac90 : 0.1;
  const frac95 = Number.isFinite(opts.frac95) ? opts.frac95 : 0.05;
  const withinBpm = Number.isFinite(opts.withinBpm) ? opts.withinBpm : 1;

  const chosen = chooseRecoveryModel(data);

  // Common Apple-style windows using whichever model rrAtAbsoluteTime picks.
  const w60 = computeRRChangeInWindow(data, 60, chosen);
  const w120 = computeRRChangeInWindow(data, 120, chosen);

  if (chosen.kind === "exp") {
    // Keep your original behavior
    const params = getDecayParams(data);
    const { A, C, tau, y0 } = params || {};
    const rrAtT = expAt(params, minute);
    const dropBpm = Number.isFinite(rrAtT) ? y0 - rrAtT : NaN;

    const t90 = timeToRemainingFraction(params, frac90);
    const t95 = timeToRemainingFraction(params, frac95);
    const tWithin = timeToWithinBpm(params, withinBpm);

    return {
      ok: true,
      model: chosen.kind,
      params,
      oneMinute: { dropBpm, rrAtT, t: minute },
      rrRecovery: { w60, w120 },
      timeToBaseline: {
        seconds90: t90,
        minutes90: Number.isFinite(t90) ? t90 / 60 : NaN,
        seconds95: t95,
        minutes95: Number.isFinite(t95) ? t95 / 60 : NaN,
        secondsWithinBpm: tWithin,
        minutesWithinBpm: Number.isFinite(tWithin) ? tWithin / 60 : NaN,
        withinBpm,
      },
    };
  }

  if (chosen.kind === "linear") {
    const p = getLinearParams(data);
    const t90 = timeToRemainingFractionLinear(data, frac90);
    const t95 = timeToRemainingFractionLinear(data, frac95);
    const tWithin = timeToWithinBpmLinear(data, withinBpm);

    // One-minute drop via linear at absolute time
    const t0Abs = getRecoveryStartAbsTime(data);
    const y0 = rrAtStart(data, chosen);
    const yAt1m = linearAtAbs(p, t0Abs + minute);
    const dropBpm =
      Number.isFinite(y0) && Number.isFinite(yAt1m) ? y0 - yAt1m : NaN;

    return {
      ok: true,
      model: chosen.kind,
      paramsLinear: p,
      baselineLinear: estimateLinearBaseline(data),
      oneMinute: { dropBpm, rrAtT: yAt1m, t: minute },
      rrRecovery: { w60, w120 },
      timeToBaseline: {
        seconds90: t90,
        minutes90: Number.isFinite(t90) ? t90 / 60 : NaN,
        seconds95: t95,
        minutes95: Number.isFinite(t95) ? t95 / 60 : NaN,
        secondsWithinBpm: tWithin,
        minutesWithinBpm: Number.isFinite(tWithin) ? tWithin / 60 : NaN,
        withinBpm,
      },
    };
  }

  // interp-only: no time-to-baseline (model-free)
  return {
    ok: true,
    model: "interp",
    rrRecovery: { w60, w120 },
    oneMinute: { dropBpm: NaN, rrAtT: NaN, t: minute },
    timeToBaseline: {
      seconds90: NaN,
      minutes90: NaN,
      seconds95: NaN,
      minutes95: NaN,
      secondsWithinBpm: NaN,
      minutesWithinBpm: NaN,
      withinBpm,
    },
  };
}

/** RR at absolute time using an explicit model choice. */
export function rrAtAbsoluteTimeUsing(data, tAbs, chosen) {
  if (!Number.isFinite(tAbs)) return NaN;
  const kind = chosen?.kind;
  const t0Abs = getRecoveryStartAbsTime(data);
  if (!Number.isFinite(t0Abs)) return NaN;

  if (kind === "exp") {
    const p = getDecayParams(data);
    if (!p) return NaN;
    const tRel = tAbs - t0Abs;
    if (tRel < 0) return NaN;
    return expAt(p, tRel);
  }

  if (kind === "linear") {
    const p = getLinearParams(data);
    if (!p) return NaN;
    return linearAtAbs(p, tAbs);
  }

  // fallback: interpolate peaks
  const times = Array.isArray(data?.peak_times_s)
    ? data.peak_times_s.map(Number)
    : null;
  const vals = Array.isArray(data?.peak_values)
    ? data.peak_values.map(Number)
    : null;
  return interpAt(times, vals, tAbs);
}

function isWeaklyConvex(arr, tol = 1e-6) {
  if (!Array.isArray(arr) || arr.length < 3) return true;
  for (let i = 1; i < arr.length - 1; i++) {
    const y0 = arr[i - 1],
      y1 = arr[i],
      y2 = arr[i + 1];
    if (!Number.isFinite(y0) || !Number.isFinite(y1) || !Number.isFinite(y2))
      continue;
    const d2 = y2 - 2 * y1 + y0; // second finite difference
    if (d2 < -tol) return false; // allow small negative noise
  }
  return true;
}
